/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Button from '../Button';
import NumberFieldInput from './NumberFieldInput';
import { VisualOnlyLabel } from '../Label';
import { IconRemoveCircleOutline, IconAddCircleOutline } from '../icons/Icons';
import announce from '../../../helpers/announce';

export default class NumberField extends React.Component {
    static propTypes = {
        /** Id for input and label. Required for 508 */
        id: PropTypes.string.isRequired,

        /** Plural descriptor for the number. Used for various accessibility
         * purposes, included the input label (i.e. Number of ${units}) */
        units: PropTypes.string.isRequired,

        /** The text for a visual label. If provided, NumberField is wrapped in
         * a `role="group"` and this label acts as the group's accessibility
         * label */
        visualLabel: PropTypes.string,

        /** If the input is required */
        visualLabelPosition: PropTypes.oneOf(['left', 'top']),

        /** If the input is required */
        isRequired: PropTypes.bool,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** If the input and buttons are disabled */
        isDisabled: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** If the decrease button is disabled. Useful if the sum of a series
         * of NumberFields has a max/min value */
        isDecreaseDisabled: PropTypes.bool,

        /** If the increase button is disabled. Useful if the sum of a series
         * of NumberFields has a max/min value */
        isIncreaseDisabled: PropTypes.bool,

        /** The maximum number allowed in the input */
        max: PropTypes.number,

        /** The minimum number allowed in the input */
        min: PropTypes.number,

        /** Change handler. Always returns a number (not a string) */
        onChange: PropTypes.func,

        /** The input value. Must be a number. Use only if fully controlling
         * NumberField */
        value: PropTypes.number,

        /** The initial value of the input. Must be a number. Use only if *not*
         * fully controlling NumberField */
        defaultValue: PropTypes.number,

        /** Adds an additional aria-describedby id. This is already used to link
         * errorText with the input */
        ariaDescribedby: PropTypes.string,

        /** Additional class added to the outer container */
        className: PropTypes.string
    };

    static defaultProps = {
        visualLabel: undefined,
        visualLabelPosition: 'left',
        isDisabled: false,
        isRequired: false,
        isInvalid: false,
        errorText: undefined,
        isDecreaseDisabled: false,
        isIncreaseDisabled: false,
        value: undefined,
        onChange: undefined,
        defaultValue: 0,
        min: 0,
        max: 99,
        className: undefined
    };

    constructor(props) {
        super(props);

        const { value, defaultValue } = this.props;

        const count = typeof value === 'number'
            ? value.toString()
            : defaultValue.toString();

        this.state = { count };
    }

    componentDidUpdate(prevProps) {
        const { value, units, max, min, onChange } = this.props;
        const { count } = this.state;

        // The input and parent's value are not synced because the input can be
        // empty or otherwise in an transitional editing state. If the parent
        // sends a new value, we check to see if it needs to be synced to the
        // input's value. If the incoming value is different and out of the
        // input's range, we send the clamped value back to the parent.

        if (typeof value === 'number' && value !== prevProps.value && value.toString() !== count) {
            const clampedValue = Math.min(Math.max(value, min), max);

            this.setState({
                count: clampedValue.toString()
            }, () => {
                if (value !== clampedValue) {
                    onChange(clampedValue);
                }
            });

            announce(`${clampedValue} ${units}`, 'polite', 'sarsa-announce-count');
        }
    }

    handleInputChange = (event) => {
        // Change the input, but don't send it up to the parent because we could
        // trigger unwanted re-renders using wrong or incomplete data. I.e. if
        // the user is entering '12', we don't want to onChange(1) and then
        // onChange(12)

        event.preventDefault();

        const value = event.target.value;
        const isNumber = /^\d+$/.test(value);

        if (isNumber) {
            this.setState({ count: value });
        }
        else if (value === '') {
            this.setState({
                count: ''
            });
        }
        else {
            announce('Error: Enter only numbers.');
        }
    }

    handleInputBlur = () => {
        // Send the value up to the parent onBlur. If the input is empty, set
        // the value to 0.

        const { onChange, min, max } = this.props;
        const { count } = this.state;
        const countNum = parseInt(count, 10);

        if (count === '') {
            const defaultCount = min > 0 ? min : 0;

            this.setState({ count: defaultCount.toString() }, () => {
                if (onChange) {
                    onChange(defaultCount);
                }
            });

            return;
        }

        // Clamp the value between the max and min
        const clampedValue = Math.min(Math.max(countNum, min), max);

        this.setState({ count: clampedValue.toString() }, () => {
            if (onChange) {
                onChange(clampedValue);
            }
        });
    }

    handleDecreaseClick = () => {
        const { onChange, min, units } = this.props;
        const { count } = this.state;

        let countNum = parseInt(count, 10) || 0;
        countNum -= 1;

        if (countNum < min) {
            countNum = min;
        }

        this.setState({
            count: countNum.toString()
        }, () => {
            announce(`${countNum} ${units}`, 'polite', 'sarsa-announce-count');

            if (onChange) {
                onChange(countNum);
            }
        });
    }

    handleIncreaseClick = () => {
        const { onChange, max, units } = this.props;
        const { count } = this.state;

        let countNum = parseInt(count, 10) || 0;
        countNum += 1;

        if (countNum > max) {
            countNum = max;
        }

        this.setState({
            count: countNum.toString()
        }, () => {
            announce(`${countNum} ${units}`, 'polite', 'sarsa-announce-count');

            if (onChange) {
                onChange(countNum);
            }
        });
    }

    render() {
        const {
            isDecreaseDisabled,
            isIncreaseDisabled,
            isRequired,
            isInvalid,
            isDisabled,
            errorText,
            max,
            min,
            units,
            id,
            className,
            visualLabel,
            visualLabelPosition,
            ariaDescribedby
        } = this.props;

        const { count } = this.state;

        const hasVisualLabel = visualLabel && visualLabel.length;
        const countNum = parseInt(count, 10);
        const outerClasses = cx({
            'sarsa-number-field-with-label-wrap': hasVisualLabel,
            'position-left': visualLabelPosition === 'left',
            'position-top': visualLabelPosition === 'top'
        });

        return (
            <div
                data-component="NumberField"
                className={className}
                role={hasVisualLabel ? 'group' : null}
                aria-labelledby={hasVisualLabel ? `group-label-${id}` : null}>
                <div className={outerClasses}>
                    {visualLabel && (
                        <VisualOnlyLabel
                            id={`group-label-${id}`}
                            isRequired={isRequired}>
                            {visualLabel}
                        </VisualOnlyLabel>
                    )}

                    <div
                        data-component="NumberField"
                        className="sarsa-number-field">
                        <Button
                            appearance="subtle"
                            size="sm"
                            onClick={this.handleDecreaseClick}
                            isDisabled={isDisabled || countNum === min || isDecreaseDisabled}
                            aria-label={`Remove ${units}`}
                            iconBeforeElement={<IconRemoveCircleOutline />} />
                        <NumberFieldInput
                            className={cx({
                                'width-3-digits': max > 99
                            })}
                            label={`Number of ${units}`}
                            pattern="\d*"
                            id={id}
                            autoComplete="off"
                            maxLength={3}
                            readOnly={isDisabled}
                            isDisabled={isDisabled}
                            isRequired={isRequired}
                            isInvalid={isInvalid}
                            errorText={errorText}
                            value={this.state.count}
                            onChange={this.handleInputChange}
                            onBlur={this.handleInputBlur}
                            ariaDescribedby={ariaDescribedby} />
                        <Button
                            appearance="subtle"
                            size="sm"
                            onClick={this.handleIncreaseClick}
                            isDisabled={isDisabled || countNum === max || isIncreaseDisabled}
                            aria-label={`Add ${units}`}
                            iconBeforeElement={<IconAddCircleOutline />} />
                    </div>
                </div>
                {errorText && (
                    <div
                        id={`${id}-error`}
                        className="sarsa-field-error-message">
                        <span className="req" aria-hidden="true">*</span>
                        {errorText}
                    </div>
                )}



            </div>
        );
    }
}
